/**
 * What a reader has chosen, read back off the page it was drawn from.
 *
 * `head/reader-settings.html` puts the settings on `<html>` as `data-cb-`
 * attributes before the page is painted, and
 * `components/reader-settings.scss` draws the page from them. Anything on this
 * side that has to agree with what a reader is looking at reads the same
 * attributes rather than asking which elements ended up visible.
 *
 * **Visibility is the one thing that cannot be the test.** Where the reading is
 * written above the characters, the line of it underneath is `hidden` and is
 * still this sentence in this reading. See `shortcodes/pinyin.html` for why it
 * is kept.
 */

/** The settings a page was drawn from, with the defaults where none were set. */
export function chosenSettings(page: Document): {
  readonly writing: string;
  readonly romanisation: string;
  readonly translations: string;
} {
  const html = page.documentElement;

  return {
    writing: html.getAttribute("data-cb-writing") ?? "zh-CN",
    romanisation: html.getAttribute("data-cb-romanisation") ?? "pinyin",
    translations: html.getAttribute("data-cb-translations") ?? "shown",
  };
}

/**
 * Whether a line belongs to the writing a reader is being shown.
 *
 * A line naming no writing belongs to every one of them, which is the
 * translation and bopomofo. A line that names writings names the ones it
 * serves, so a row shared by Taiwan and Hong Kong answers for both.
 */
export function servesWriting(line: Element, writing: string): boolean {
  const serves = line.getAttribute("data-cb-writing");

  return serves === null || serves.split(" ").includes(writing);
}

/**
 * Whether a line is the reading a reader asked for.
 *
 * Bopomofo and pinyin are the same reading in two scripts, so a block holds
 * both and a copy takes one. Every other line is neither and is taken whatever
 * was chosen.
 */
export function servesRomanisation(
  line: Element,
  romanisation: string,
): boolean {
  const wanted = romanisation === "bopomofo" ? "zhuyin" : "pinyin";
  const other = romanisation === "bopomofo" ? "pinyin" : "zhuyin";

  return !line.classList.contains(other) || line.classList.contains(wanted);
}

/**
 * Whether a line survives what a reader chose about the translations.
 *
 * Every line but one does. The English under a 例句 is `.yingwen`. A reader who
 * has put the translations away is looking at a block of two lines, and two
 * lines are what the button beside it copies.
 *
 * The other `.yingwen` on this site is the meaning in a 词典 archive row. It
 * carries no `.liju-element` and reaches no copy button.
 * `components/reader-settings` is where a gloss of a word and a translation of
 * a sentence are told apart.
 */
export function servesTranslations(
  line: Element,
  translations: string,
): boolean {
  return translations !== "hidden" || !line.classList.contains("yingwen");
}
